/**
 * Jotai atom tracing instrumentation.
 *
 * Uses `store.sub(atom, listener)` to subscribe to atom value changes and log
 * transitions with full context (prev/next values, write sources, session IDs).
 *
 * Why store.sub instead of wrapping store.set:
 *   store.sub is Jotai's official subscription API. It doesn't require replacing
 *   the store or monkey-patching, keeps instrumentation isolated, and works with
 *   all atom types including derived atoms. The listener fires after Jotai finishes
 *   a batch of synchronous updates.
 *
 * Datadog integration:
 *   Transition events are sent directly to datadogLogs.logger to avoid the
 *   double-logging caused by @moment/logging's forwardConsoleLogs: "all".
 *   Lifecycle messages (watch started/stopped) still use the Logger class.
 *   Every event includes three correlation IDs:
 *     - traceId: unique per watchAtom() call (groups events for one watch session)
 *     - appSessionId: UUID generated once at module load (constant for app lifetime)
 *     - ddSessionId: read from datadogLogs.getInternalContext() (ties to DD browser session)
 *
 *   To upgrade to full APM traces later, add @datadog/browser-rum and replace
 *   Logger calls with datadogRum.startSpan() / datadogRum.addAction(). The
 *   traceId and session IDs will already be in place for correlation.
 */
import { datadogLogs } from "@datadog/browser-logs";
import type { Atom } from "jotai";

import { Logger } from "@moment/logging";

import { store } from "../store";
import type { AtomTransitionEvent, Unwatch, WatchAtomOptions } from "./atom-tracer.types";

const logger = new Logger("atom-tracer");

// ─── Session IDs ─────────────────────────────────────────────────────────────

/** App-level session ID, generated once at module load. */
const appSessionId = crypto.randomUUID();

export function getAppSessionId(): string {
	return appSessionId;
}

/**
 * Reads the Datadog browser session ID. Returns undefined if DD is not
 * initialized (e.g. local dev) or if getInternalContext is unavailable.
 */
export function getDatadogSessionId(): string | undefined {
	try {
		return datadogLogs.getInternalContext()?.session_id;
	} catch {
		return undefined;
	}
}

// ─── Safe Serialization ──────────────────────────────────────────────────────

const CIRCULAR_REF = "[Circular]";
const FUNCTION_REF = "[Function]";
const ABORT_CONTROLLER_REF = "[AbortController]";
const TRUNCATED = "[Truncated]";

/**
 * Safely serializes a value for logging, handling:
 * - Circular references (via WeakSet)
 * - Functions (replaced with "[Function]")
 * - AbortController (replaced with "[AbortController]")
 * - Error objects (serialized as name + message)
 * - Date objects (to ISO string)
 * - Depth limiting
 */
export function safeSerialize(value: unknown, maxDepth: number = 3): unknown {
	const seen = new WeakSet();

	function walk(val: unknown, depth: number): unknown {
		if (val === null || val === undefined) {
			return val;
		}
		if (typeof val === "boolean" || typeof val === "number" || typeof val === "string") {
			return val;
		}
		if (typeof val === "bigint") {
			return val.toString();
		}
		if (typeof val === "symbol") {
			return val.toString();
		}
		if (typeof val === "function") {
			return FUNCTION_REF;
		}

		if (val instanceof Date) {
			return val.toISOString();
		}
		if (val instanceof Error) {
			return `[${val.name}: ${val.message}]`;
		}
		if (typeof AbortController !== "undefined" && val instanceof AbortController) {
			return ABORT_CONTROLLER_REF;
		}

		if (depth >= maxDepth) {
			return TRUNCATED;
		}

		if (typeof val === "object") {
			if (seen.has(val as object)) {
				return CIRCULAR_REF;
			}
			seen.add(val as object);
		}

		if (Array.isArray(val)) {
			return val.map((item) => walk(item, depth + 1));
		}

		if (val instanceof Map) {
			const result: Record<string, unknown> = {};
			for (const [k, v] of val) {
				result[String(k)] = walk(v, depth + 1);
			}
			return result;
		}

		if (val instanceof Set) {
			return Array.from(val).map((item) => walk(item, depth + 1));
		}

		if (typeof val === "object") {
			const result: Record<string, unknown> = {};
			for (const key of Object.keys(val as Record<string, unknown>)) {
				try {
					result[key] = walk((val as Record<string, unknown>)[key], depth + 1);
				} catch {
					result[key] = "[Unreadable]";
				}
			}
			return result;
		}

		return String(val);
	}

	return walk(value, 0);
}

// ─── Null Preservation ───────────────────────────────────────────────────────

/**
 * Datadog's browser-logs SDK strips null/undefined attributes from context.
 * This replaces them with the string "(null)" so they remain visible as
 * searchable attributes in the Datadog log explorer.
 */
function preserveNulls(obj: unknown): unknown {
	if (obj === null || obj === undefined) {
		return "(null)";
	}
	if (typeof obj !== "object") {
		return obj;
	}
	if (Array.isArray(obj)) {
		return obj.map(preserveNulls);
	}
	const result: Record<string, unknown> = {};
	for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
		result[k] = preserveNulls(v);
	}
	return result;
}

// ─── Trace ID Generation ─────────────────────────────────────────────────────

let traceCounter = 0;

function generateTraceId(label: string): string {
	const timestamp = Date.now().toString(36);
	const counter = (traceCounter++).toString(36);
	return `atom-trace_${label}_${timestamp}_${counter}`;
}

// ─── Write Context Tracking ──────────────────────────────────────────────────

/**
 * Tracks the debugLabel of the top-level atom that initiated the current
 * store.set() call chain. In Jotai v2, store.sub listeners fire synchronously
 * during store.set(), so this value is still set when our listener reads it.
 *
 * Supports nested writes: if atom A's setter calls set(atomB, ...), we keep
 * the outermost context (atom A) since that's the most useful for debugging.
 */
let writeContextStack: string[] = [];
let storePatched = false;

export function getCurrentWriteContext(): string[] {
	return [...writeContextStack];
}

/**
 * Patches store.set to record write context. Call once at app startup.
 * Safe to call multiple times (no-ops after the first).
 */
export function enableWriteTracking(): void {
	if (storePatched) {
		return;
	}
	storePatched = true;

	const originalSet = store.set.bind(store);

	store.set = (atom: { debugLabel?: string }, ...args: unknown[]) => {
		const label = atom.debugLabel ?? "[unlabeled atom]";
		writeContextStack.push(label);
		try {
			return originalSet(atom, ...args);
		} finally {
			writeContextStack.pop();
		}
	};

	logger.info("Write tracking enabled", { appSessionId });
}

// ─── Core Watch Function ─────────────────────────────────────────────────────

/**
 * Watches an atom for value transitions and logs each change.
 *
 * The listener receives no arguments from Jotai — we read the new value with
 * store.get(atom) and compare with the cached previous value. All reads are
 * wrapped in try/catch so tracing never crashes the app.
 *
 * @param targetAtom - The Jotai atom to watch
 * @param options - Configuration for filtering, serialization, and logging
 * @returns An unsubscribe function
 */
export function watchAtom<T>(targetAtom: Atom<T>, options: WatchAtomOptions<T> = {}): Unwatch {
	const {
		label = targetAtom.debugLabel ?? "unknown-atom",
		shouldLog,
		level = "info",
		maxSerializationDepth = 3,
		serialize,
	} = options;

	const traceId = generateTraceId(label);
	let transitionIndex = 0;

	function serializeValue(value: T | undefined): unknown {
		if (serialize) {
			try {
				return serialize(value);
			} catch {
				return "[Serialization error]";
			}
		}
		return safeSerialize(value, maxSerializationDepth);
	}

	// Capture initial value safely
	let previousValue: T | undefined;
	try {
		previousValue = store.get(targetAtom);
	} catch {
		previousValue = undefined;
	}

	logger.info("Watch started", {
		traceId,
		appSessionId,
		ddSessionId: getDatadogSessionId(),
		label,
		initialValue: serializeValue(previousValue),
	});

	const unsubscribe = store.sub(targetAtom, () => {
		try {
			let nextValue: T | undefined;
			try {
				nextValue = store.get(targetAtom);
			} catch (err) {
				logger.warn("Failed to read atom value during trace", {
					traceId,
					appSessionId,
					label,
					error: err instanceof Error ? err.message : String(err),
				});
				nextValue = undefined;
			}

			// Apply predicate filter
			if (shouldLog && !shouldLog(previousValue, nextValue)) {
				previousValue = nextValue;
				return;
			}

			const writeContext = storePatched ? getCurrentWriteContext() : undefined;

			const event: AtomTransitionEvent = {
				traceId,
				appSessionId,
				ddSessionId: getDatadogSessionId(),
				label,
				timestamp: new Date().toISOString(),
				previousValue: serializeValue(previousValue),
				nextValue: serializeValue(nextValue),
				writeContext,
				transitionIndex: transitionIndex++,
			};

			// Send directly to datadogLogs to get proper structured attributes.
			// We avoid the Logger class here because @moment/logging's
			// forwardConsoleLogs: "all" would cause a duplicate, stringified log.
			// We also replace nulls with "(null)" so DD preserves the attributes.
			const ddLogFn = datadogLogs.logger[level];
			if (ddLogFn) {
				ddLogFn.call(
					datadogLogs.logger,
					`[atom-tracer] Atom transition`,
					preserveNulls(event) as Record<string, unknown>
				);
			}

			previousValue = nextValue;
		} catch (err) {
			// Never crash the app — swallow and log the tracing error itself
			try {
				logger.error("Atom tracer internal error", {
					traceId,
					appSessionId,
					label,
					error: err instanceof Error ? err.message : String(err),
				});
			} catch {
				// Absolute last resort — silently fail
			}
		}
	});

	return () => {
		logger.info("Watch stopped", {
			traceId,
			appSessionId,
			label,
			totalTransitions: transitionIndex,
		});
		unsubscribe();
	};
}
